import React, { useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";

import _ from "lodash";
import { useSelector } from "react-redux";
import delivery from "../../assets/icons/delivery.png";
import dineInActive from "../../assets/icons/dinein-white.png";
import dineIn from "../../assets/icons/dinein.png";
import driveThru from "../../assets/icons/drive-thru.png";
import takeawayActive from "../../assets/icons/take-away-white.png";
import takeaway from "../../assets/icons/take-away.png";
import { components } from "../../components";
import dateHelper from "../../helpers/dateHelper";
import { hooks } from "../../hooks";
import { Routes, TabScreens } from "../../routes";
import apiService from "../../service/ApiService";
import { RootState } from "../../store";
import { actions } from "../../store/actions";
import {
  setOpenBottemSheet,
  setOrderMode,
  setSitesOrderModes
} from "../../store/slices/appSlice";
import { setSelectedSiteId } from "../../store/slices/selectedSiteSlice";

import constants from "../../helpers/constants";
import Enums from "../../helpers/enums";
import utils from "../../helpers/utils";
import { clearCart } from "../../store/slices/cartSlice";
import { clearCustomizations } from "../../store/slices/lookupSlice";
import type {
  Banner,
  BannerType,
  MenuType
} from "../../types";
import { Autoplay, Pagination, Navigation } from 'swiper/modules';

export const Home: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();

  const [opacity, setOpacity] = useState<number>(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedSite, setSelectedSite] = useState();
  const [categories, setCategories] = useState<[]>([]);
  const [showSheet, setShowSheet] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>();
  const [supportedOrderMode, setSupportedOrderMode] = useState<string[]>([]);
  const [banner, setBanner] = useState<BannerType>({
    allBaners: [],
    groupedBanners: [],
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const loading: boolean = isLoading;

  const orderMode = useSelector((state: RootState) => state.appReducer.orderMode);
  const selectedSiteId = useSelector((state: RootState) => state.selectedSiteReducer.selectedSiteId);
  const sites = useSelector((state: RootState) => state.screenReducer.sites);
  const authState = useSelector((state: RootState) => state.authReducer.auth);
  const sitesOrderModes = useSelector((state: RootState) => state.appReducer.sitesOrderModes);
  const openBottomSheet = useSelector((state: RootState) => state.appReducer.openBottomSheet);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);

  useEffect(() => {
    if (openBottomSheet) {
      setShowSheet(true);
    }
  }, [openBottomSheet]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (selectedSiteId !== -1) {
      setSelectedSite(utils.getSelectedSite(selectedSiteId, sites));
      getBanners();
    }
    else {
      setLoading(false);
    }
  }, [sites, selectedSiteId]);

  useEffect(() => {
    if (selectedSiteId && orderMode) {
      getCategories();
    }
  }, [selectedSiteId, orderMode]);

  useEffect(() => {
    if (!_.isEmpty(selectedSite)) {
      getOrderModesFromSiteId();
    }
  }, [selectedSite]);

  const getOrderModesFromSiteId = async () => {
    const { data } = await apiService.getAssignedMenusBySiteIdAsync(
      selectedSiteId
    );
    if (data && data.length > 0) {
      dispatch(setSitesOrderModes(data));
    }
  };

  useEffect(() => {
    if (selectedSiteId !== -1) {
      const orderModesFromSite = _.find(
        sites,
        (site) => site.siteId === selectedSiteId
      );
      if (orderModesFromSite && orderModesFromSite.supportedOrderModes) {
        setSupportedOrderMode(
          _.split(orderModesFromSite.supportedOrderModes, ",")
        );
        setActiveTab(
          orderMode
            ? orderMode
            : _.split(orderModesFromSite.supportedOrderModes, ",")[0]
        );
        dispatch(
          setOrderMode(
            orderMode
              ? orderMode
              : _.split(orderModesFromSite.supportedOrderModes, ",")[0]
          )
        );
        setSupportedOrderMode(
          _.split(orderModesFromSite.supportedOrderModes, ",")
        );
      }
    } else {
      setShowSheet(true);
    }
  }, [selectedSiteId]);

  useEffect(() => {
    if (banner.allBaners && banner.allBaners.length > 0) {
      filterBanners(banner.allBaners);
    }
  }, [banner.allBaners, authState.user, orderMode]);

  const filterBanners = (data: any[]) => {
    const filteredBannerData = data.filter(
      (banner: {
        supportedOrderModes: string;
        openingTime: any;
        closingTime: any;
        visibility: boolean;
        navigationType: number;
      }) => {
        const isOrderModeSupported =
          !banner.supportedOrderModes ||
          banner.supportedOrderModes.split(",").includes(orderMode);
        const isWithinTimeRange =
          !banner.openingTime ||
          !banner.closingTime ||
          dateHelper.isCurrentTimeWithinRange(
            banner.openingTime,
            banner.closingTime
          );
        const isVisibleToUser =
          banner.visibility === false ||
          banner.visibility === (authState.user !== null);
        const isSignupBannerVisible =
          banner.navigationType === 3 && authState.user !== null ? false : true;
        return (
          isOrderModeSupported &&
          isWithinTimeRange &&
          isVisibleToUser &&
          isSignupBannerVisible
        );
      }
    );

    //TODO

    // const groupedBanners: GroupedBanner[] = Object.entries(
    //   _.groupBy(filteredBannerData, "bannerType")
    // ).map(([type, items]) => ({
    //   bannerType: type,
    //   banners: items,
    // }));
    setBanner({
      ...banner,
      groupedBanners: filteredBannerData,
    });
  };

  const getCategories = async () => {
    setLoading(true);
    const { data } = await apiService.getCategoriesAsync(selectedSiteId, orderMode);
    if (data) {
      let categories = data.filter(
        (category: { openingTime: any; closingTime: any; isPrivate: any }) => {
          const isWithinTime =
            !category.openingTime ||
            !category.closingTime ||
            dateHelper.isCurrentTimeWithinRange(
              category.openingTime,
              category.closingTime
            );
          const isAccessible =
            authState && authState.user && !authState.user.isGuest
              ? true
              : !category.isPrivate;
          return isWithinTime && isAccessible;
        }
      );
      setCategories(categories);
      setLoading(false);
    }
  };

  const getBanners = async () => {
    const { data } = await apiService.getBannersAsync(selectedSiteId);
    if (data && data.banners) {
      setBanner({
        ...banner,
        allBaners: data.banners,
      });
    }
    setLoading(false);
  }

  const handleSlideChange = (swiper: any) => {
    setActiveSlide(swiper.activeIndex);
  };

  const onPressBanner = async (item: Banner) => {
    const filterMode = sitesOrderModes.find(
      (item) => item.orderMode === orderMode
    );
    if (item.type === Enums.comboType.menuItem) {
      const categoryItem = {
        ...item,
        ...(filterMode && filterMode.menuId && { menuId: filterMode.menuId }),
      };
      navigate(Routes.ItemDetail, { state: { categoryItem } });
    } else if (item.navigationType === Enums.navigationType.signUp) {
      navigate(Routes.SignIn);
    } else if (item.category) {
      if (item.category.externalId) {
        //navigate(CjConstants.screen.comboList, { category: item.category });
      } else {
        navigate(Routes.MenuList, {
          state: { category: item.category },
        });
      }
    }
  };

  const renderBanner = (): JSX.Element | null => {
    if (isLoading || banner.allBaners.length === 0) {
      return null;
    }

    return (
      <section
        style={{ marginBottom: 30, marginTop: 10, position: "relative" }}
      >
        <Swiper
          slidesPerView={"auto"}
          spaceBetween={30}
          rewind={true}
          pagination={{ clickable: true }}
          navigation={true}
          style={{ justifyContent: 'center' }}
          mousewheel={true}
          centeredSlides={true}
          autoplay={{
            delay: 10000,
            disableOnInteraction: true,
          }}
          modules={[Autoplay, Pagination, Navigation]}
          onSlideChange={handleSlideChange}
        >
          {banner.groupedBanners.map((banner: any) => {
            return (
              <SwiperSlide key={banner.bannerId}>
                <img
                  key={banner.bannerId}
                  alt={"banner"}
                  src={banner.imagePath}
                  style={{ width: "100%", height: isMobile ? "30vh" : "40vh", borderRadius: 10, }}
                  className="clickable"
                  onClick={() => onPressBanner(banner)}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            bottom: 27,
            zIndex: 1,
            width: "100%",
            gap: 6,
          }}
        >
          {banner.groupedBanners.map((banner: any, index) => {
            return (
              <div
                key={index}
                style={{
                  width: 8,
                  height: activeSlide === index ? 20 : 8,
                  borderRadius: 10,
                  backgroundColor:
                    activeSlide === index
                      ? "var(--white-color)"
                      : `rgba(255, 255, 255, 0.5)`,
                }}
              />
            );
          })}
        </div>
      </section>
    );
  };

  const renderMenu = (): JSX.Element => {
    return (
      <section style={{ marginBottom: 30, marginTop: 8 }}>
        <components.BlockHeading
          title="We offer"
          viewAllOnClick={() => {
            dispatch(actions.setScreen(TabScreens.Menu));
          }}
          containerStyle={{ marginLeft: 20, marginRight: 10, marginBottom: 14 }}
        />
        <div style={{ width: "100%" }}>
          <Swiper
            spaceBetween={10}
            slidesPerView={"auto"}
            pagination={{ clickable: true }}
            navigation={true}
            mousewheel={true}
          >
            {categories.map(
              (category: any, index: number, array: MenuType[]) => {
                const isLast = index === array.length - 1;

                return (
                  <SwiperSlide key={index} style={{ width: "auto" }}>
                    <button
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: 10,
                        border: "1px solid",
                        marginLeft: index === 0 ? 20 : 0,
                        marginRight: isLast ? 20 : 0,
                        marginBottom: 4,
                        position: "relative",
                        backgroundColor: "var(--white-color)",
                        borderColor: "var(--white-color)",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                      }}
                      onClick={() => {
                        navigate(Routes.MenuList, {
                          state: { category: category },
                        });
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          bottom: 4,
                          left: 4,
                          right: 4,
                          textAlign: "center",
                          backgroundColor: "var(--white-color)",
                          borderRadius: "0 0 0 0",
                          color: "var(--main-color)",
                        }}
                      >
                        <img
                          src={utils.getImagePath(category.imagePath)}
                          alt={category.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: 10,
                          }}
                        />
                        <span
                          style={{
                            position: "absolute",
                            bottom: 0,
                            paddingTop: 5,
                            width: '100%',
                            textAlign: "center",
                            backgroundColor: "var(--white-color)",
                            borderRadius: "0 0 0 0",
                            color: "var(--main-color)",
                            fontWeight: 500
                          }}
                          className="t12 number-of-lines-1"
                        >
                          {category.name}
                        </span>
                      </span>
                    </button>
                  </SwiperSlide>
                );
              }
            )}
          </Swiper>
        </div>
      </section>
    );
  };

  const handleOrderMode = (orderMode: string) => {
    setActiveTab(orderMode);
    dispatch(setOrderMode(orderMode));
    dispatch(clearCart());
    dispatch(clearCustomizations());
  };

  const renderOrderModes = (): JSX.Element => {
    return (
      <div
        style={{
          display: "flex",
          width: "40%",
          gap: "8px",
          margin: 20,
        }}
      >
        {supportedOrderMode.map((orderMode, index) => (
          <button
            id={index.toString()}
            className="t14"
            key={orderMode}
            onClick={() => handleOrderMode(orderMode)}
            style={{
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              gap: "5px",
              padding: "8px",
              justifyContent: "center",
              borderRadius: "15px",
              border: "none",
              background:
                activeTab === orderMode ? "var(--main-turquoise)" : "#ffffff",
              color: activeTab === orderMode ? "#fff" : "#333",
              fontWeight: activeTab === orderMode ? 600 : 400,
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 2px 0px rgba(0,0,0,0.15)",
            }}
          >
            {isMobile ? null : (
              <img
                src={
                  orderMode === constants.orderMode.eatIn
                    ? (activeTab === constants.orderMode.eatIn ? dineInActive : dineIn)
                    : orderMode === constants.orderMode.walkIn
                      ? (activeTab === constants.orderMode.walkIn ? takeawayActive : takeaway)
                      : orderMode === constants.orderMode.delivery
                        ? (activeTab === constants.orderMode.delivery ? delivery : delivery)
                        : (activeTab === constants.orderMode.driveThru ? driveThru : driveThru)
                }
                alt={orderMode}
                style={{
                  width: 20,
                  fill: activeTab === orderMode ? "#fff" : "#333",
                  height: 20,
                  objectFit: "contain",
                }}
              />
            )}
            {orderMode}
          </button>
        ))}
      </div>
    );
  };

  const renderContent = (): JSX.Element => {
    if (loading) return <components.Loader />;

    return (
      <main className="scrollable">
        {supportedOrderMode &&
          supportedOrderMode.length > 0 &&
          renderOrderModes()}
        {renderMenu()}
        {renderBanner()}
      </main>
    );
  };

  const renderSites = (): JSX.Element => {
    const handelCardSelection = (site: any) => {
      dispatch(setSelectedSiteId(site.siteId));
      setShowSheet(false);
      dispatch(setOpenBottemSheet(false));
    };
    return (
      <main className="scrollable" style={{ paddingLeft: 2, paddingRight: 2 }}>
        <div
          style={{
            display: isMobile ? "grid" : "flex",
            flexDirection: "column",
            gap: "8px",
            marginTop: "16px",
            marginBottom: "16px",
            borderRadius: "25px",
          }}
        >
          {!_.isEmpty(sites) &&
            _.map(sites, (site, index) => {

              return (
                <components.SiteCard
                  key={index}
                  title={site.siteName}
                  status={`${site.isSiteOpen ? "Open" : "Closed"}`}
                  time={`${site.openingTime} - ${site.closingTime}`}
                  address={site}
                  distance={_.round(site.distance)}
                  selected={selectedSiteId === site.siteId}
                  onClick={() => handelCardSelection(site)}
                ></components.SiteCard>
              )
            })}
        </div>
      </main>
    );
  };

  return (
    <div id="screen" style={{ opacity }}>
      {renderContent()}
      {sites && (
        <components.BottomSheet
          maxHeight="80vh"
          open={showSheet}
          onClose={() => setShowSheet(false)}
          title="Find Your Store"
          disabled={!selectedSiteId}
        >
          <div
            style={{
              maxHeight: "calc(80vh - 120px)",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            {renderSites()}
          </div>
        </components.BottomSheet>
      )}
    </div>
  );
};
